import type { ReactElement } from "react";
import { Link } from "react-router-dom";

import { ArticleHero } from "../ArticleHero";
import HeroImg from "../../assets/images/newborn-pink-studio-portrait.webp";
import newbornCalmImage from "../../assets/images/newborn-autumn-close-up.webp";
import newborn3 from "../../assets/images/newborn-baby-girl-floral-basket-portrait.webp";
import newborn4 from "../../assets/images/newborn-baby-girl-pink-dress-portrait.webp";
import newbornSessionBagImage from "../../assets/images/newborn-fall-pumpkin-basket.webp";
import newbornGreenImage from "../../assets/images/newborn-green-stroller-portrait.webp";
import newbornPumpkinImage from "../../assets/images/newborn-pumpkin-wrap-portrait.webp";
import newbornBearImage from "../../assets/images/newborn-sleeping-bear-portrait.webp";
import { ArticlePhoto } from "./ArticlePhoto";
import { PreparationPlanner } from "./components/PreparationPlanner";
import type { PreparationPlannerStage } from "./components/PreparationPlanner";
import { UiIcon } from "./components/UiIcons";
import { GuideCtaActions } from "./components/GuideCtaActions";
import "./NewbornPages.scss";
import "./NewbornArticle.scss";
import "./HowToPrepareForNewbornSession.scss";

const articleImages: Record<"hero" | "clothing" | "sessionBag" | "family" | "calmBaby", string | undefined> = {
  hero: HeroImg,
  clothing: newborn3,
  sessionBag: newbornSessionBagImage,
  family: newborn4,
  calmBaby: newbornCalmImage,
};

const preparationStages: PreparationPlannerStage[] = [
  {
    label: "A few days before",
    title: "Share the details that matter",
    items: [
      "Tell me which rooms have the clearest open space and natural light",
      "Choose simple clothing for parents and siblings",
      "Set aside anything personal you would like photographed",
    ],
  },
  {
    label: "The evening before",
    title: "Prepare a few familiar things",
    items: [
      "Keep diapers, wipes and your usual feeding supplies nearby",
      "Clear one small area that we can evaluate together",
      "Place everyone’s clothing together",
    ],
  },
  {
    label: "Session morning",
    title: "Follow your baby’s rhythm",
    items: [
      "Feed your baby as you normally would",
      "Dress your baby in a loose outfit that opens easily",
      "Leave room in the schedule so you do not feel rushed",
    ],
  },
  {
    label: "When I arrive",
    title: "Let me take it from here",
    items: [
      "Tell me how your baby has been feeding and sleeping",
      "We will choose the best space before I set up the lighting",
      "Expect pauses for feeding, changing and cuddling",
    ],
  },
];

const sessionBagItems = [
  "Diapers and wipes",
  "Your usual feeding supplies",
  "A pacifier if your baby already uses one",
  "Spare clothes for your baby and parents",
  "Any personal item we discussed before the session",
  "Water and snacks for parents during longer sessions",
];

const relatedGuides = [
  {
    title: "Best Age for Newborn Photos",
    href: "/resources/newborn/best-age-for-newborn-photos/",
    image: newbornGreenImage,
    imageAlt: "Sleeping newborn baby posed in a green wooden stroller",
  },
  {
    title: "What Happens During a Newborn Session",
    href: "/resources/newborn/what-happens-during-a-newborn-session/",
    image: newbornBearImage,
    imageAlt: "Sleeping newborn baby in a warm bear-themed portrait",
  },
  {
    title: "What If My Baby Doesn’t Sleep?",
    href: "/resources/newborn/what-happens-during-a-newborn-session/#baby-awake",
    image: newbornPumpkinImage,
    imageAlt: "Newborn baby wrapped in rust-colored fabric beside autumn pumpkins",
  },
];

export const HowToPrepareForNewbornSession = (): ReactElement => (
  <main className="newborn-page newborn-article-page newborn-preparation-guide">
    <article>
      <ArticleHero
        breadcrumbs={[
          { label: "Home", to: "/" },
          { label: "Resources", to: "/resources/" },
          { label: "Newborn", to: "/resources/newborn/" },
          { label: "How to Prepare for Your Newborn Photo Session" },
        ]}
        category="Newborn guide"
        description="A practical guide to preparing your home, baby and family for a calm in-home newborn photo session."
        imageAlt="Sleeping newborn baby prepared for a newborn photography session"
        imagePosition="center 42%"
        imageSrc={articleImages.hero ?? HeroImg}
        readingTime="8 min read"
        title="How to Prepare for Your Newborn Photo Session"
        updatedAt="2026-09-07"
        updatedLabel="Updated September 2026"
      />

      <div className="newborn-shell newborn-article-layout">
        <aside className="newborn-article-callout newborn-article-callout--answer">
          <span className="newborn-article-callout__icon">
            <UiIcon name="star" />
          </span>
          <div>
            <h2>The most important thing to know</h2>
            <p>
              Preparing for an in-home newborn photo session does not mean making your home perfect or changing your
              baby’s routine. I come to your Charlotte-area home with professional lighting, wraps, outfits and the
              essential photography equipment. You only need to keep your baby’s familiar things nearby, prepare
              clothing for family portraits and leave a small area that we can evaluate together. Feeding, changing and
              soothing are expected parts of the session.
            </p>
          </div>
        </aside>

        <nav aria-label="In this guide" className="newborn-article-toc">
          <h2>In this guide</h2>
          <ol>
            <li>
              <a href="#preparation-plan">Your preparation plan</a>
            </li>
            <li>
              <a href="#feeding-and-sleep">Feeding and sleep before the session</a>
            </li>
            <li>
              <a href="#what-baby-wears">What should your baby wear?</a>
            </li>
            <li>
              <a href="#family-preparation">Preparing parents and siblings</a>
            </li>
            <li>
              <a href="#what-to-keep-nearby">What to keep nearby</a>
            </li>
            <li>
              <a href="#do-not-worry">What you do not need to worry about</a>
            </li>
          </ol>
        </nav>

        <section className="newborn-article-section newborn-preparation-plan" id="preparation-plan">
          <header>
            <p className="newborn-article-kicker">A SIMPLE PHOTO-DAY PLAN</p>
            <h2>Prepare a little at a time</h2>
            <p>
              Most of the work can be done in a few small steps. This planner keeps the practical details together so
              the morning of your session feels familiar rather than hectic.
            </p>
          </header>
          <PreparationPlanner stages={preparationStages} />
        </section>

        <section className="newborn-article-section newborn-preparation-feature" id="feeding-and-sleep">
          <div>
            <p className="newborn-article-kicker">FOLLOW YOUR NORMAL RHYTHM</p>
            <h2>Feeding and sleep before the session</h2>
            <p>
              Please feed your baby when they are hungry. If the timing works naturally, a full feed shortly before we
              begin can help your baby settle—but there is no need to delay a feed or make a hungry baby wait for the
              camera.
            </p>
            <p>
              You also do not need to keep your baby awake for hours beforehand. That can leave everyone overtired.
              Follow your usual routine, and tell me how the morning has gone when I arrive. We can begin with a feed, a
              wrap or awake photographs depending on what your baby needs.
            </p>
          </div>
          <ArticlePhoto
            alt="Calm newborn baby resting before a photography session"
            className="newborn-preparation-feature__photo"
            label="PHOTO: A CALM START"
            src={articleImages.calmBaby}
          />
          <blockquote>
            <p>There is no perfect schedule to protect. The session is planned around how your baby feels that day.</p>
          </blockquote>
        </section>

        <section className="newborn-article-section newborn-article-split" id="what-baby-wears">
          <div>
            <p className="newborn-article-kicker">COMFORT FIRST</p>
            <h2>What should your baby wear?</h2>
            <p>
              Dress your baby in a loose, simple outfit that opens easily and does not need to be pulled over the head.
              Avoid tight socks, waistbands or anything likely to leave marks on the skin shortly before the session.
            </p>
            <p>
              Wraps, outfits and props used in the newborn photographs are provided. If you would like to include a
              meaningful blanket, small heirloom or another personal piece, send me a photograph beforehand. We can
              decide whether it fits the session safely and naturally.
            </p>
          </div>
          <ArticlePhoto
            alt="Wrapped newborn baby girl posed in a floral basket"
            className="newborn-article-split__photo"
            label="PHOTO: WRAPS & OUTFITS"
            src={articleImages.clothing}
          />
        </section>

        <section
          className="newborn-article-section newborn-article-split newborn-article-split--reverse"
          id="family-preparation"
        >
          <ArticlePhoto
            alt="Sleeping newborn baby girl wearing a soft pink dress"
            className="newborn-article-split__photo"
            label="PHOTO: FAMILY CLOTHING"
            src={articleImages.family}
          />
          <div>
            <p className="newborn-article-kicker">PARENTS AND SIBLINGS</p>
            <h2>Preparing the rest of the family</h2>
            <p>
              Choose clothing that feels comfortable and coordinates without matching exactly. Soft neutrals and gentle
              textures keep attention on faces and connection. Large logos, very bright colors and busy patterns can
              pull attention away from the baby.
            </p>
            <p>
              For young siblings, keep the explanation positive and brief: they are coming to cuddle the baby and take a
              few photographs, not perform for the camera. Bring a quiet activity and a snack, and avoid promising that
              they must smile. I will work with their personality and energy throughout the session.
            </p>
          </div>
        </section>

        <section className="newborn-article-section newborn-packing-list" id="what-to-keep-nearby">
          <div>
            <p className="newborn-article-kicker">KEEP THE FAMILIAR THINGS CLOSE</p>
            <h2>What to keep nearby during your newborn session</h2>
            <p>
              There is no need to pack for a trip or buy special props. Keep these everyday items within easy reach. I
              bring the wraps, outfits, lighting and photography equipment we planned to use.
            </p>
            <ul>
              {sessionBagItems.map((item) => (
                <li key={item}>
                  <UiIcon name="check" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <ArticlePhoto
            alt="Everyday baby essentials prepared for an in-home newborn photo session"
            className="newborn-packing-list__photo"
            label="PHOTO: BABY ESSENTIALS"
            src={articleImages.sessionBag}
          />
        </section>

        <section className="newborn-article-section newborn-no-worries" id="do-not-worry">
          <header>
            <p className="newborn-article-kicker">THIS IS ALL NORMAL</p>
            <h2>What you do not need to worry about</h2>
          </header>
          <div>
            <article>
              <span>01</span>
              <h3>Your baby cries</h3>
              <p>We pause, work out what your baby needs and begin again when they are comfortable.</p>
            </article>
            <article>
              <span>02</span>
              <h3>Your baby stays awake</h3>
              <p>
                Awake portraits, small expressions and connection with parents can be beautiful parts of the gallery.
              </p>
            </article>
            <article>
              <span>03</span>
              <h3>You need to feed again</h3>
              <p>Newborns often feed more than expected. The session includes time for that.</p>
            </article>
            <article>
              <span>04</span>
              <h3>Someone’s clothing gets messy</h3>
              <p>Spit-up and diaper surprises happen. That is why a simple backup outfit is useful.</p>
            </article>
          </div>
        </section>

        <section className="newborn-article-cta newborn-preparation-cta">
          <div>
            <p className="newborn-article-kicker">PLAN THE SESSION THAT FITS YOUR FAMILY</p>
            <h2>Ready to choose your newborn session?</h2>
            <p>
              Review the newborn options and pricing. If you have already booked and need help preparing, send me your
              question instead.
            </p>
          </div>
          <GuideCtaActions category="newborn" />
        </section>

        <section className="newborn-related-guides">
          <header>
            <h2>Related newborn guides</h2>
            <Link to="/resources/newborn/">
              View all guides <UiIcon name="arrow" />
            </Link>
          </header>
          <div>
            {relatedGuides.map((guide, index) => (
              <Link className="newborn-related-guide" key={guide.href} to={guide.href}>
                <ArticlePhoto
                  alt={guide.imageAlt}
                  className={`newborn-related-guide__photo newborn-related-guide__photo--${index + 1}`}
                  label="PHOTO"
                  src={guide.image}
                />
                <span>{guide.title}</span>
                <UiIcon name="arrow" />
              </Link>
            ))}
          </div>
        </section>
      </div>
    </article>
  </main>
);

export default HowToPrepareForNewbornSession;
